import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {
  Login,
  Dashboard,
  OrderForm,
  Accessories,
  Profile,
  ClientsManagement,
  ArticlesManagement,
  AgentsManagement,
  ClientOrdersManagement,
  OrderConfirmation,
  MaintenanceMode,
  MaintenancePage,
  ProductCategorySelector,
  OrderDetails,
  OrderSummary,
  DrainageProcessPage,
  AccessoriesProcessPage,
  AccessoriesConfirmation,
  RicambiPage,
  CapitolatoForm,
  ProductCapitolatoCategorySelector,
  StoricoCapitolati,
  OrdersStatus,
  InviiCertificazioni,
  DocumentsPage,
  AdminDocumentsManagement,
  AlbaOrderForm,
  PensilineOrderForm,
  SferaNOrderForm
} from './pages';
import { OrderProvider } from './context/OrderContext';
import { ProtectedRoute, UpdateBanner } from './layout';
import { AuthProvider } from './context/AuthContext';
import { api } from './services/api';
import { useVersionCheck } from './hooks/useVersionCheck';

const App = () => {
  const { updateAvailable, latestVersion, dismiss } = useVersionCheck();
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(true);
  const [, setMaintenanceMode] = useState(false);

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await api.getMaintenanceStatus();
        if (response.status === 'success' && response.maintenance) {
          setMaintenanceMode(response.maintenance.enabled);
        }
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
      } finally {
        setIsCheckingMaintenance(false);
      }
    };

    checkMaintenanceMode();
  }, []);

  if (isCheckingMaintenance) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <>
    <Router>
      <AuthProvider>
        <OrderProvider>
          <div className="App">
            <Routes>
              <Route path="/" element={
                <div className="flex items-center justify-center" style={{ minHeight: '100vh' }}>
                  <div className="card" style={{ width: '100%' }}>
                    <Login />
                  </div>
                </div>
              } />
              <Route path="/login" element={
                <div className="flex items-center justify-center" style={{ minHeight: '100vh' }}>
                  <div className="card" style={{ width: '100%' }}>
                    <Login />
                  </div>
                </div>
              } />
              <Route path="/maintenance-page" element={<MaintenancePage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/product-categories"
                element={
                  <ProtectedRoute>
                    <ProductCategorySelector />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order"
                element={
                  <ProtectedRoute>
                    <OrderForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-alba"
                element={
                  <ProtectedRoute>
                    <AlbaOrderForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-pensiline"
                element={
                  <ProtectedRoute>
                    <PensilineOrderForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-sferan"
                element={
                  <ProtectedRoute>
                    <SferaNOrderForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accessories"
                element={
                  <ProtectedRoute>
                    <Accessories />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accessories-confirmation"
                element={
                  <ProtectedRoute>
                    <AccessoriesConfirmation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ricambi"
                element={
                  <ProtectedRoute>
                    <RicambiPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <ProtectedRoute>
                    <ClientsManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/articles"
                element={
                  <ProtectedRoute>
                    <ArticlesManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents"
                element={
                  <ProtectedRoute>
                    <AgentsManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/client-orders"
                element={
                  <ProtectedRoute>
                    <ClientOrdersManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders-status"
                element={
                  <ProtectedRoute>
                    <OrdersStatus />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invii-certificazioni"
                element={
                  <ProtectedRoute>
                    <InviiCertificazioni />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-details"
                element={
                  <ProtectedRoute>
                    <OrderDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-summary"
                element={
                  <ProtectedRoute>
                    <OrderSummary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/confirmation"
                element={
                  <ProtectedRoute>
                    <OrderConfirmation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maintenance"
                element={
                  <ProtectedRoute>
                    <MaintenanceMode />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/drainage-process"
                element={
                  <ProtectedRoute>
                    <DrainageProcessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accessories-process/:accessoryType"
                element={
                  <ProtectedRoute>
                    <AccessoriesProcessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/capitolato-categories"
                element={
                  <ProtectedRoute>
                    <ProductCapitolatoCategorySelector />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/capitolato/form"
                element={
                  <ProtectedRoute>
                    <CapitolatoForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/storico-capitolati"
                element={
                  <ProtectedRoute>
                    <StoricoCapitolati />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <ProtectedRoute>
                    <DocumentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/documents"
                element={
                  <ProtectedRoute>
                    <AdminDocumentsManagement />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </OrderProvider>
      </AuthProvider>
    </Router>
    {updateAvailable && <UpdateBanner latestVersion={latestVersion} onDismiss={dismiss} />}
    </>
  );
};

export default App;
